<template>
  <info-panel
    v-if="flowBranch"
    :item="flowBranch"
    :title="title"
    :sections="sections"
    :extra-settings="extraSettings"
    @update:sections="updateSections($event)"
    @update:extra-settings="updateExtraSettings($event)"
    @close="emit('close')"
  >
    <template #label-value>
      {{ $t("labels.strength") }}
    </template>
    <template #heading-value>
      {{ $t("labels.strength") }}: {{ formatNumber(calculatedValue?.value) }}
      {{ calculatedValue?.unit }}
    </template>
    <template #value>
      <div class="info-panel__table">
        <span class="label">{{ $t("words.currentValue") }}</span>
        <span>{{ formatNumber(calculatedValue?.value) }} {{ calculatedValue?.unit }}</span>

        <span class="label">{{ $t("words.startValue") }}</span>
        <span>{{ formatNumber(startValue?.value) }} {{ startValue?.unit }}</span>

        <span class="label">{{ $t("labels.valueRange") }}</span>
        <span>{{ formatNumber(startValue?.minValue) }} – {{ formatNumber(startValue?.maxValue) }}</span>
      </div>
    </template>

    <template #label-flow>
      {{ $t("objectNames.flow") }}
    </template>
    <template #flow v-if="settingsValues.showEmptySections || flow">
      <button-item v-if="flow" @click="openOverlay(flow)">
        {{ flowTitle }}
      </button-item>
    </template>

    <template #label-element>
      {{ $t("objectNames.element") }}
    </template>
    <template #element v-if="settingsValues.showEmptySections || element">
      <button-item v-if="element" @click="openOverlay(element)">
        <span v-if="elementTitle">{{ elementTitle }}</span>
        <span v-else class="placeholder-text">
          {{ $t("labels.unnamed") }}
        </span>
      </button-item>
    </template>
  </info-panel>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { mapValues } from "@/helpers/utility/CollectionFunctions";
import { formatNumber } from "@/helpers/utility/Formatting";

import ButtonItem from "@core/input/ButtonItem.vue";
import InfoPanel from "@editor/ui/InfoPanel.vue";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useFlowStore } from "../flow/flows.store";
import { useFlowBranchStore } from "../flow/flowBranches.store";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";

import type { InfoPanelSetting } from "@editor/store/infoPanels.store";
import type { FlowBranchItem } from "../flow/FlowBranchItem";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";

const props = defineProps<{
  flowBranch: FlowBranchItem | null;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: OverlayDialogItemRequest): void;
}>();

const flowStore = useFlowStore();
const infoPanelStore = useInfoPanelStore();
const simulationViewStore = useSimulationViewStore();

const sections = reactive({
  value: true,
  flow: true,
  element: true
});
const extraSettings = reactive({
  showEmptySections: { type: "check" as const, value: true }
});

const startValue = computed(() => {
  if (props.flowBranch == null) return null;
  return useFlowBranchStore().expressionValues[props.flowBranch.uid] ?? null;
});

const calculatedValue = computed(() => {
  if (props.flowBranch == null) return null;
  return simulationViewStore.elementValues[props.flowBranch.uid];
});

const targets = computed(() => {
  return useTargetStore().items;
});
const resources = computed(() => {
  return useResourceStore().items;
});
const indices = computed(() => {
  return useIndexStore().items;
});
const items = computed(() => {
  return {
    target: targets.value,
    resource: resources.value,
    index: indices.value
  };
});

// Flow

const flow = computed(() => {
  if (props.flowBranch == null) return null;
  return flowStore.items[props.flowBranch.flowId];
});

const flowTitle = computed(() => {
  if (props.flowBranch == null) return "";
  return flowStore.titles[props.flowBranch?.flowId] ?? "";
});

// Element

const element = computed(() => {
  if (props.flowBranch == null) return null;
  return items.value[props.flowBranch.elementType]?.[props.flowBranch.elementId] ?? null;
});

const elementTitle = computed(() => {
  if (element.value == null) return "...";
  return element.value.title;
});

// Title

const title = computed(() => {
  if (props.flowBranch == null) return "";

  const ft = '"' + flowTitle.value + '"';

  if (props.flowBranch.branchType === "source") {
    return elementTitle.value + " ⇒ " + ft;
  }
  else {
    return ft + " ⇒ " + elementTitle.value;
  }
});

function openOverlay(item: OverlayDialogItemRequest) {
  emit("select", item);
}

function updateSections(value: { [section: string]: boolean }) {
  Object.assign(sections, value);
  infoPanelStore.sections.flowBranch = sections;
}

function loadSections() {
  const savedSections = infoPanelStore.sections?.flowBranch || {};
  for (const key in sections) {
    if (key in savedSections) {
      sections[key as (keyof typeof sections)] = savedSections[key];
    }
  }
}

loadSections();


function updateExtraSettings(value: { [setting: string]: InfoPanelSetting }) {
  Object.assign(extraSettings, value);
  infoPanelStore.extraSettings.flowBranch = extraSettings;
}

function loadExtraSettings() {
  const savedExtraSettings = infoPanelStore.extraSettings?.flowBranch || {};
  for (const key in extraSettings) {
    if (key in savedExtraSettings) {
      extraSettings[key as (keyof typeof extraSettings)] = { ...(savedExtraSettings[key] as any) };
    }
  }
}

const settingsValues = computed(() => {
  return mapValues(extraSettings, (setting) => setting.value);
});

loadExtraSettings();
</script>

<style></style>
